import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import apiService from "../../../services/api";
import { filterBySearchTerm } from "../../../utils/search";
import { getInstituicaoLogo } from "../../../utils/instituicaoLogos";
import emAnaliseIcon from "../../../assets/em-analise.png";
import concedidoIcon from "../../../assets/concedido.png";
import naoConcedidoIcon from "../../../assets/naoConcedido.png";
import eyeIcon from "../../../assets/visibility.png";
import ConcessaoDetalhe from "./ConcessaoDetalhe";
import { usePopup } from "../../common/Popup";
import '../users/user-table.css';

const Outer = styled.div`
  display: flex;
  justify-content: center;
  background: #f9f9f9;
  font-family: public-sans, sans-serif;
`;

const Container = styled.div`
  width: 100%;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 2px;
  padding: 15px;
  background: #f2f0f0;
  border-radius: 8px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #555;
  height: 24px;

  &:first-child {
    margin-left: 25px;
  }
`;

const LegendIcon = styled.img`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

const LegendBadge = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 11px;
  color: white;
  flex-shrink: 0;
  background: ${props => {
    if (props.type === 'PF') return '#6AB8A8';
    if (props.type === 'PE') return '#FF9F1F';
    if (props.type === 'PM') return '#9B59B6';
    return '#555555';
  }};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  ${({ $concedidas }) =>
    $concedidas &&
    `
      th:nth-child(4),
      td:nth-child(4) {
        min-width: 180px;
      }

      th:nth-child(6),
      td:nth-child(6) {
        width: 100px;
        min-width: 100px;
        padding-left: 10px;
        padding-right: 10px;
      }

      th:nth-child(8),
      td:nth-child(8) {
        width: 90px;
        padding-left: 10px;
        padding-right: 10px;
      }
    `}
`;

const TableHeader = styled.thead`
  background: #f2f0f0;
`;

const TableHeaderCell = styled.th`
  padding: 16px 20px;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: #6ab8a8;
  border-bottom: 1px solid #dfe3e8;
  border-right: 2px solid #dfe3e8;

  &:last-child {
    border-right: none;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #e6e6e6;
  transition: background-color 0.2s;

  &:nth-child(odd) {
    background: #e8f5f3;
  }

  &:nth-child(even) {
    background: #ffffff;
  }

  &:hover {
    background: #f9fefa;
  }
`;

const TableCell = styled.td`
  padding: 12px 20px;
  font-size: 16px;
  color: #555555;
  border-right: 2px solid #dfe3e8;
  line-height: 1.4;
  font-family: 'Public Sans', sans-serif;
  font-weight: 500;

  &:last-child {
    border-right: none;
  }
`;

const CenteredTableCell = styled(TableCell)`
  text-align: center;
`;

const InstitutionCell = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const LogoWrapper = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #6ab8a8;
  color: white;
  font-weight: bold;
  font-size: 12px;
  flex-shrink: 0;
`;

const LogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StatusIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 8px;
`;

const StatusText = styled.span`
  font-weight: 600;
  color: ${props => {
    if (props.status === 'CONCEDIDO' || props.status === 'CONFIRMADA') return '#059669';
    if (props.status === 'RECUSADO') return '#dc2626';
    return '#374151';
  }};
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const EmptyMessage = styled.div`
  padding: 32px;
  text-align: center;
  color: #555;
  font-size: 15px;
`;

const formatTipoAtividade = (tipo) => {
  const tipos = {
    'ESTAGIO_OBRIGATORIO': 'Estág. Obrigatório',
    'ESTAGIO_SUPERVISIONADO': 'Estágio Supervisionado',
    'INTERNATO_MEDICO': 'Internato Médico',
    'AULA_PRATICA': 'Aula Prática'
  };
  return tipos[tipo] || tipo;
};

const formatPeriodo = (periodos) => {
  if (!periodos || periodos.length === 0) return '-';
  const primeiro = periodos[0];
  const ultimo = periodos[periodos.length - 1];
  const dateOptions = { day: '2-digit', month: '2-digit', year: '2-digit' };
  const inicio = new Date(primeiro.dataInicio).toLocaleDateString('pt-BR', dateOptions);
  const fim = new Date(ultimo.dataFim).toLocaleDateString('pt-BR', dateOptions);
  return `${inicio} a ${fim}`;
};

const getStatusIcon = (status) => {
  if (status === 'CONCEDIDO' || status === 'CONFIRMADA') return concedidoIcon;
  if (status === 'RECUSADO') return naoConcedidoIcon;
  return emAnaliseIcon;
};

const getStatusText = (status) => {
  if (status === 'CONCEDIDO') return 'CONCEDIDO';
  if (status === 'CONFIRMADA') return 'CONFIRMADO';
  if (status === 'RECUSADO') return 'NÃO CONCEDIDO';
  return 'PARA ANÁLISE';
};

const renderLogo = (instituicao) => {
  if (!instituicao) return null;

  const explicitLogo = instituicao.logoUrl;
  const mappedLogo = getInstituicaoLogo({
    naturezaJuridica: instituicao.naturezaJuridica,
    esfera: instituicao.esfera,
  });

  const finalLogo = explicitLogo || mappedLogo;

  if (finalLogo) {
    return (
      <LogoWrapper>
        <LogoImg src={finalLogo} alt={instituicao.sigla || instituicao.nomeRegistroMEC} />
      </LogoWrapper>
    );
  }

  // Fallback para iniciais
  const nome = instituicao.sigla || instituicao.nomeRegistroMEC || '??';
  return (
    <LogoWrapper>
      {nome
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()}
    </LogoWrapper>
  );
};

const ConcessoesContent = ({ user, sortOrder, searchTerm = '', statusFilter = null, vagaId = null }) => {
  const navigate = useNavigate();
  const popup = usePopup();
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [selectedVaga, setSelectedVaga] = useState(null);

  const fetchSolicitacoes = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getSolicitacoesVaga(statusFilter);
      const data = result.data ? result.data : result;
      setSolicitacoes(data);
    } catch (err) {
      setError(err.message || "Falha ao carregar solicitações");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchSolicitacoes();
    }
  }, [user, statusFilter]);

  const handleConceder = async (solicitacao) => {
    const solicitacaoId = solicitacao.id;
    const quantidadeVagas = solicitacao.quantidadeAlunosConcedidos
      ?? solicitacao.quantidadeAlunos
      ?? 0;
    const confirmed = await popup.confirm({
      icon: "info",
      message: `Deseja conceder esta solicitação? Serão concedidas ${quantidadeVagas} vagas. Você pode ver as informações completas da solicitação clicando em visualizar.`,
    });
    if (!confirmed) return;

    setActionLoading(prev => ({ ...prev, [solicitacaoId]: 'conceder' }));

    try {
      await apiService.concederSolicitacaoVaga(solicitacaoId);
      await popup.alert({
        icon: "success",
        message: "Solicitação concedida com sucesso!",
      });
      fetchSolicitacoes();
    } catch (error) {
      console.error('Erro ao conceder solicitação:', error);
      await popup.alert({
        icon: "warning",
        title: "Erro ao conceder solicitação",
        message: error.message || "Tente novamente.",
      });
    } finally {
      setActionLoading(prev => ({ ...prev, [solicitacaoId]: null }));
    }
  };

  const handleRecusar = async (solicitacaoId) => {
    const confirmed = await popup.confirm({
      icon: "info",
      message: "Deseja recusar esta solicitação?",
    });
    if (!confirmed) return;

    setActionLoading(prev => ({ ...prev, [solicitacaoId]: 'recusar' }));

    try {
      await apiService.recusarSolicitacaoVaga(solicitacaoId);
      await popup.alert({
        icon: "success",
        message: "Solicitação recusada com sucesso!",
      });
      fetchSolicitacoes();
    } catch (error) {
      console.error('Erro ao recusar solicitação:', error);
      await popup.alert({
        icon: "warning",
        title: "Erro ao recusar solicitação",
        message: error.message || "Tente novamente.",
      });
    } finally {
      setActionLoading(prev => ({ ...prev, [solicitacaoId]: null }));
    }
  };

  const normalizedVagaId = vagaId !== null && vagaId !== undefined ? Number(vagaId) : null;
  const solicitacoesFiltradasPorVaga = normalizedVagaId
    ? solicitacoes.filter((s) => {
        const idVaga = s.vaga?.id ?? s.vagaId;
        return idVaga !== null && idVaga !== undefined && Number(idVaga) === normalizedVagaId;
      })
    : solicitacoes;

  const searchedSolicitacoes = searchTerm
    ? filterBySearchTerm(solicitacoesFiltradasPorVaga, searchTerm, [
        (s) => s.curso?.campus?.instituicoes?.[0]?.sigla || s.curso?.campus?.instituicoes?.[0]?.nomeRegistroMEC || '',
        (s) => s.curso?.curso || '',
        (s) => formatTipoAtividade(s.vaga?.tipoAtividade) || ''
      ])
    : solicitacoesFiltradasPorVaga;

  const statusOrder = {
    PENDENTE: 0,
    CONCEDIDO: 1,
    CONFIRMADA: 2,
    RECUSADO: 3,
  };
  const filteredSolicitacoes = sortOrder
    ? [...searchedSolicitacoes].sort((a, b) => {
        const aOrder = statusOrder[a.status || "PENDENTE"] ?? 99;
        const bOrder = statusOrder[b.status || "PENDENTE"] ?? 99;
        return sortOrder === "asc" ? aOrder - bOrder : bOrder - aOrder;
      })
    : searchedSolicitacoes;

  if (selectedVaga) {
    return (
      <ConcessaoDetalhe
        vaga={selectedVaga}
        onVoltar={() => setSelectedVaga(null)}
      />
    );
  }

  if (loading) {
    return (
      <Outer>
        <Container>
          <EmptyMessage>Carregando...</EmptyMessage>
        </Container>
      </Outer>
    );
  }

  if (error) {
    return (
      <Outer>
        <Container>
          <EmptyMessage>{error}</EmptyMessage>
        </Container>
      </Outer>
    );
  }

  return (
    <Outer>
      <Container>
        <LegendContainer>
          <LegendItem>
            <LegendIcon src={concedidoIcon} alt="Concedido" />
            <span>Concedido</span>
          </LegendItem>
          <LegendItem>
            <LegendIcon src={naoConcedidoIcon} alt="Não Concedido" />
            <span>Não Concedido</span>
          </LegendItem>
          <LegendItem>
            <LegendIcon src={emAnaliseIcon} alt="Para Análise" />
            <span>Para Análise</span>
          </LegendItem>
          <LegendItem>
            <LegendBadge type="PF">PF</LegendBadge>
            <span>Escola Pública Federal</span>
          </LegendItem>
          <LegendItem>
            <LegendBadge type="PE">PE</LegendBadge>
            <span>Escola Pública Estadual</span>
          </LegendItem>
          <LegendItem>
            <LegendBadge type="PM">PM</LegendBadge>
            <span>Escola Pública Municipal</span>
          </LegendItem>
          <LegendItem>
            <LegendBadge type="PP">PP</LegendBadge>
            <span>Escola Particular Privada</span>
          </LegendItem>
        </LegendContainer>

        <TableWrapper>
          <Table $concedidas={statusFilter === 'CONCEDIDO'}>
            <TableHeader>
              <tr>
                <TableHeaderCell>INSTITUIÇÃO</TableHeaderCell>
                <TableHeaderCell>CURSO</TableHeaderCell>
                <TableHeaderCell>TIPO</TableHeaderCell>
                <TableHeaderCell>SETOR</TableHeaderCell>
                <TableHeaderCell>PERÍODO</TableHeaderCell>
                <TableHeaderCell style={{ whiteSpace: 'nowrap' }}>
                  {statusFilter === 'PENDENTE'
                    ? 'VAGAS SOLICITADAS'
                    : statusFilter === 'CONCEDIDO'
                      ? <><span>VAGAS</span><br /><span>CONCEDIDAS</span></>
                      : 'VAGAS'}
                </TableHeaderCell>
                <TableHeaderCell>STATUS</TableHeaderCell>
                <TableHeaderCell>VISUALIZAR</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {filteredSolicitacoes.length === 0 ? (
                <tr>
                  <TableCell colSpan="8" style={{ textAlign: 'center', padding: '32px' }}>
                    Nenhuma solicitação encontrada.
                  </TableCell>
                </tr>
              ) : (
                filteredSolicitacoes.map((solicitacao) => {
                  const instituicao = solicitacao.curso?.campus?.instituicoes?.[0];
                  const status = solicitacao.status || 'PENDENTE';
                  const canEdit = status === 'PENDENTE';

                  return (
                    <TableRow key={solicitacao.id}>
                      <TableCell>
                        <InstitutionCell>
                          <StatusIcon src={getStatusIcon(status)} alt={getStatusText(status)} />
                          {renderLogo(instituicao)}
                          <span>{instituicao?.sigla || instituicao?.nomeRegistroMEC || '-'}</span>
                        </InstitutionCell>
                      </TableCell>
                      <CenteredTableCell>{solicitacao.curso?.curso || '-'}</CenteredTableCell>
                      <CenteredTableCell>{formatTipoAtividade(solicitacao.vaga?.tipoAtividade) || '-'}</CenteredTableCell>
                      <CenteredTableCell>{solicitacao.vaga?.unidadeAssistencial?.nome || '-'}</CenteredTableCell>
                      <CenteredTableCell>{formatPeriodo(solicitacao.periodos)}</CenteredTableCell>
                      <CenteredTableCell>
                        {solicitacao.quantidadeAlunosConcedidos
                          ?? solicitacao.quantidadeAlunos
                          ?? '-'}
                      </CenteredTableCell>
                      <CenteredTableCell>
                        {canEdit ? (
                          <ActionsCell>
                            <button
                              className="btn-reject"
                              onClick={() => handleRecusar(solicitacao.id)}
                              disabled={actionLoading[solicitacao.id]}
                            >
                              {actionLoading[solicitacao.id] === 'recusar' ? 'Recusando...' : 'Não conceder ✕'}
                            </button>
                            <button
                              className="btn-approve"
                              onClick={() => handleConceder(solicitacao)}
                              disabled={actionLoading[solicitacao.id]}
                            >
                              {actionLoading[solicitacao.id] === 'conceder' ? 'Concedendo...' : 'Conceder ✓'}
                            </button>
                          </ActionsCell>
                        ) : (
                          <StatusText status={status}>
                            {getStatusText(status)}
                          </StatusText>
                        )}
                      </CenteredTableCell>
                      <CenteredTableCell>
                        <button
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px'
                          }}
                          title="Visualizar vaga e solicitações"
                          onClick={() => {
                            if (status === 'CONFIRMADA' && user?.cargoId === 4) {
                              navigate(`/Confirmadas/${solicitacao.id}`);
                              return;
                            }
                            setSelectedVaga(solicitacao.vaga);
                          }}
                        >
                          <img src={eyeIcon} alt="Visualizar" style={{ width: 22, height: 22 }} />
                        </button>
                      </CenteredTableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </Container>
    </Outer>
  );
};

export default ConcessoesContent;
